import { z } from 'zod';
import { ClassTierSchema } from './effects.js';
import { StatusKindSchema } from './common.js';
import { MAX_SALVO_SPREAD_RADIANS } from '@prism/sim';
import {
  BiomeIdSchema,
  ClassIdSchema,
  DamageKindSchema,
  EnemyIdSchema,
  HexColor,
  MapIdSchema,
  MAP_HEIGHT,
  MAP_WIDTH,
  MutatorIdSchema,
  TargetingModeSchema,
  TierSchema,
  TowerIdSchema,
} from './common.js';
import { SynergyEffectSchema } from './effects.js';

// ── Classes (TRD-02 §5) ───────────────────────────────────────────────────────────────

export const ClassDefSchema = z.object({
  id: ClassIdSchema,
  name: z.string().min(2),
  /** The tint applied to tower bodies. Ten same-shaped towers make this load-bearing. */
  color: HexColor,
  accentColor: HexColor,
  glyph: z.string().min(1).max(2),
  description: z.string().min(8),
  tiers: z
    .tuple([ClassTierSchema, ClassTierSchema, ClassTierSchema])
    .refine(
      (tiers) => tiers[0].threshold === 2 && tiers[1].threshold === 4 && tiers[2].threshold === 6,
      { message: 'tiers must be declared in 2 / 4 / 6 order' },
    ),
});

// ── Towers (TRD-02 §6) ────────────────────────────────────────────────────────────────

/** Kinds that resolve on the fire tick and never travel (TRD-05 §5). */
const HITSCAN_KINDS = ['beam', 'laser', 'chain'] as const;

export const ProjectileDefSchema = z
  .object({
    kind: z.enum(['bullet', 'homing', 'beam', 'laser', 'lob', 'chain', 'zone']),
    /** Tiles per second. Meaningless for hitscan kinds, which resolve instantly. */
    speed: z.number().nonnegative(),
    radius: z.number().positive(),
    /** Radians per second, homing only. */
    turnRate: z.number().nonnegative().default(0),
    aoeRadius: z.number().nonnegative().default(0),

    // ── Firing approach (TRD-05 §5.1) ────────────────────────────────────────────────
    // Orthogonal to `kind`. A homing salvo and a piercing lob are both authorable
    // without adding a kind, which is what stops the enum growing a combinatorial tail.

    /** Projectiles per trigger pull, in a symmetric fan. Damage is DIVIDED across them. */
    salvo: z.number().int().min(1).max(12).default(1),
    /** Total fan angle in radians. Wider than this stops reading as one shot. */
    spreadRadians: z.number().min(0).max(MAX_SALVO_SPREAD_RADIANS).default(0),
    /** Shots back-to-back before the full cooldown. 1 = no burst. */
    burstCount: z.number().int().min(1).max(8).default(1),
    /** Ticks between the shots of a burst. */
    burstIntervalTicks: z.number().int().min(1).max(30).default(4),
    /** Enemies a travelling shot passes through before it dies. */
    pierce: z.number().int().min(0).max(8).default(0),
    /** Tiles/s². Non-zero makes a missile: slow off the rail, fast on arrival. */
    acceleration: z.number().min(0).max(80).default(0),
    /** `laser` only: damage multiplier gained per damage tick held on the same enemy. */
    beamRampPerTick: z.number().min(0).max(0.5).default(0),
    beamRampMax: z.number().min(1).max(6).default(1),

    // ── Zone weapon (B-18/B-19), read only when kind is 'zone' ───────────────────────
    /** Telegraph ticks before the first strike. */
    zoneWindupTicks: z.number().int().min(1).max(180).default(30),
    /** Ticks the zone stays active once the windup has elapsed. */
    zoneActiveTicks: z.number().int().min(1).max(600).default(120),
    /** Ticks between strikes while active. */
    zoneStrikeEveryTicks: z.number().int().min(1).max(120).default(20),
    /** Tiles/s the zone drifts toward its mark. Zero = static (B-18); non-zero tracks (B-19). */
    zoneTrackSpeed: z.number().min(0).max(4).default(0),
  })
  .refine(
    (p) => (HITSCAN_KINDS as readonly string[]).includes(p.kind) || p.kind === 'zone' || p.speed > 0,
    {
      message: 'only hitscan and zone kinds may have zero speed — everything else has to travel',
    },
  )
  .refine((p) => p.kind !== 'zone' || p.aoeRadius > 0, {
    message: 'a zone IS its area — aoeRadius is its radius and must be positive',
  })
  .refine((p) => p.kind !== 'zone' || (p.salvo === 1 && p.pierce === 0), {
    message: 'zones neither fan nor travel — salvo and pierce do not apply',
  })
  .refine((p) => p.zoneTrackSpeed === 0 || p.kind === 'zone', {
    message: 'only a zone can track — zoneTrackSpeed means nothing on other kinds',
  })
  .refine((p) => p.salvo === 1 || p.spreadRadians > 0, {
    message: 'a salvo of more than one needs a spread, or every pellet flies the same line',
  })
  .refine((p) => p.salvo === 1 || !(HITSCAN_KINDS as readonly string[]).includes(p.kind), {
    message: 'hitscan kinds resolve on one line and cannot fan a salvo',
  })
  .refine((p) => p.acceleration === 0 || p.speed > 0, {
    message: 'acceleration needs a launch speed to scale — a missile cannot start at rest',
  })
  .refine((p) => p.beamRampPerTick === 0 || p.kind === 'laser', {
    message: 'only a laser ramps: the ramp is a reward for HOLDING a beam on one target',
  })
  .refine((p) => p.beamRampMax === 1 || p.beamRampPerTick > 0, {
    message: 'a ramp cap with no ramp per tick does nothing',
  })
  .refine((p) => p.pierce === 0 || !(HITSCAN_KINDS as readonly string[]).includes(p.kind), {
    message: 'hitscan kinds do not travel, so they have nothing to pierce',
  });

export const TowerStatsSchema = z.object({
  /** Zero ONLY on a support tower — a TowerDefSchema refine enforces the pairing. */
  damage: z.number().min(0),
  damageKind: DamageKindSchema,
  /** Seconds. Converted to integer ticks at fire time (TRD-05 R-05-09).
   *  Zero ONLY on a support tower, where it is the never-fires discriminator (B-15). */
  attackInterval: z.number().min(0),
  /** Tiles. A support tower's range is its aura reach (via `radiusFromRange`). */
  range: z.number().positive(),
  projectile: ProjectileDefSchema,
  defaultTargeting: TargetingModeSchema,
});

export const TowerDefSchema = z.object({
  id: TowerIdSchema,
  name: z.string().min(2),
  tier: TierSchema,
  /**
   * EXACTLY two, distinct (R-02-06). This is what makes the synergy web dense rather
   * than a checklist: 30 towers × 2 classes = 60 slots over 10 classes = 6 each, so
   * every class can reach its tier-6 threshold and multiple tier-4s are realistic.
   */
  classes: z
    .array(ClassIdSchema)
    .length(2)
    .refine((c) => c[0] !== c[1], { message: 'a tower cannot carry the same class twice' }),
  glyph: z.string().min(1).max(2),
  description: z.string().min(8).max(200),
  base: TowerStatsSchema,
  /**
   * Multiplies DAMAGE only (R-02-07). Range has its own curve below.
   */
  starScaling: z.tuple([z.literal(1), z.number().min(1), z.number().min(1)]),
  /**
   * How range grows with stars (playtest request) — R-02-07 AMENDED.
   *
   * That rule read "star scaling multiplies damage only", on the reasoning that scaling
   * range would retroactively invalidate a placement the player had already committed to.
   * The reasoning holds for range that SHRINKS. This curve only ever grows — the schema
   * refuses a value below 1 — so a tile chosen for its coverage still has that coverage
   * and then some. Nothing a player already decided is taken back.
   *
   * It is also the only thing a merge can give a support tower, whose aura IS its range
   * and which fires nothing to scale damage on.
   */
  rangeScaling: z.tuple([z.literal(1), z.number().min(1), z.number().min(1)]),
  onFire: z.array(SynergyEffectSchema).default([]),
  onHit: z.array(SynergyEffectSchema).default([]),
  onKill: z.array(SynergyEffectSchema).default([]),
  /**
   * A pure buffer (backlog B-15): never targets, never fires. Its whole power is
   * `innateEffects`; its cost is the board slot and the class counts it occupies.
   */
  support: z.boolean().default(false),
  /**
   * The spec's authored identity (B-15/B-17), evaluated at synergy recompute with the
   * tower as sole member: a support tower's aura, a broodmother's innate `summon`.
   */
  innateEffects: z.array(SynergyEffectSchema).default([]),
})
  .refine((t) => t.support || (t.base.damage > 0 && t.base.attackInterval > 0), {
    message: 'a fighting tower needs positive damage and attackInterval',
  })
  .refine((t) => !t.support || (t.base.damage === 0 && t.base.attackInterval === 0), {
    message: 'a support tower does not fight — damage and attackInterval must be 0',
  })
  .refine((t) => !t.support || t.innateEffects.length > 0, {
    message: 'a support tower with no innate effects is a dead board slot',
  });

// ── Enemies (TRD-02 §7) ───────────────────────────────────────────────────────────────

export const EnemyFlagSchema = z.enum([
  'flying',
  'splitter',
  'mender',
  'shielded',
  'boss',
  'leaper',
]);

export const EnemyDefSchema = z
  .object({
    id: EnemyIdSchema,
    name: z.string().min(2),
    hp: z.number().positive(),
    /** FLAT subtraction against physical damage, not a percentage (TRD-05 R-05-19). */
    armor: z.number().min(0),
    /** Tiles per second. */
    speed: z.number().positive(),
    bounty: z.number().min(0),
    leakDamage: z.number().min(1),
    /** Hit radius in tiles. */
    size: z.number().positive(),
    flags: z.array(EnemyFlagSchema).default([]),
    splitInto: z.object({ id: EnemyIdSchema, count: z.number().int().positive() }).optional(),
    /**
     * The children TOGETHER inherit this fraction of the parent's max hp, split evenly.
     *
     * For a boss only. An ordinary splitter's children are authored at a sensible size
     * relative to their parent and need nothing here; a boss's are not and cannot be,
     * because the child archetype is shared with the wave that spawns it plain.
     */
    splitHpFraction: z.number().gt(0).lte(1).optional(),
    shieldPerHit: z.number().positive().optional(),
    /** A breakable shield BAR on top of `hp`: while it holds, health cannot be touched.
     *  Emptying it freezes the enemy for `shieldDownTicks`, which is the only window its
     *  health is reachable, then the bar returns to full. */
    shieldHp: z.number().positive().optional(),
    shieldDownTicks: z.number().int().positive().max(600).optional(),
    healRadius: z.number().positive().optional(),
    healPercentPerSecond: z.number().positive().optional(),
    /** `leaper` (B-16): hop `leapTiles` forward every `leapEveryTicks` of the enemy's age. */
    leapEveryTicks: z.number().int().positive().optional(),
    leapTiles: z.number().positive().optional(),
    /** Statuses that bounce off entirely — Prism's long-claimed stun immunity. */
    statusImmunities: z.array(StatusKindSchema).optional(),
    /** Ticks spent rising out of the ground; immobile and untouchable until then. */
    emergeTicks: z.number().int().positive().max(600).optional(),
    /** Ticks between seeded blinks to the mirrored half of the map. */
    teleportEveryTicks: z.number().int().positive().optional(),
    /**
     * A plate across its front. `frontalCos` is the COSINE of the arc's half-angle — 0.5
     * is 60 either side of dead ahead — authored rather than computed so no trigonometry
     * runs anywhere near the hashed path.
     */
    frontalCos: z.number().gt(0).lt(1).optional(),
    frontalReduction: z.number().gt(0).lt(1).optional(),
    /** Disabler: reach, blast radius, how long the towers it catches stay dark, cadence. */
    disableRange: z.number().positive().optional(),
    disableRadius: z.number().positive().optional(),
    disableTicks: z.number().int().positive().max(1200).optional(),
    disableEveryTicks: z.number().int().positive().optional(),
    sprite: z.string().min(1),
  })
  .refine((e) => e.splitHpFraction === undefined || e.splitInto !== undefined, {
    message: 'splitHpFraction means nothing without splitInto',
  })
  .refine((e) => !e.flags.includes('splitter') || e.splitInto !== undefined, {
    message: 'splitter enemies must declare splitInto',
  })
  // Either field alone is inert — a bar that never drops, or a window that never opens.
  // The same silent no-op the leap fields are guarded against below.
  .refine((e) => (e.shieldHp === undefined) === (e.shieldDownTicks === undefined), {
    message: 'shieldHp and shieldDownTicks only mean anything together',
  })
  // All four or none. Three of them describe a shot that never fires, or a cadence with
  // nothing to fire — the same silent no-op the leap fields are guarded against.
  .refine(
    (e) =>
      [e.disableRange, e.disableRadius, e.disableTicks, e.disableEveryTicks].filter(
        (v) => v !== undefined,
      ).length %
        4 ===
      0,
    { message: 'a disabler needs disableRange, disableRadius, disableTicks and disableEveryTicks' },
  )
  // An arc with no reduction blocks nothing; a reduction with no arc has nowhere to apply.
  .refine((e) => (e.frontalCos === undefined) === (e.frontalReduction === undefined), {
    message: 'frontalCos and frontalReduction only mean anything together',
  })
  .refine(
    (e) =>
      !e.flags.includes('leaper') || (e.leapEveryTicks !== undefined && e.leapTiles !== undefined),
    { message: 'leaper enemies must declare leapEveryTicks and leapTiles' },
  )
  .refine((e) => e.flags.includes('leaper') || e.leapEveryTicks === undefined, {
    message: 'leap fields without the leaper flag would silently do nothing',
  })
  .refine((e) => !e.flags.includes('leaper') || !e.flags.includes('flying'), {
    message: 'a flying enemy has no path to leap along',
  })
  .refine((e) => !e.flags.includes('shielded') || e.shieldPerHit !== undefined, {
    message: 'shielded enemies must declare shieldPerHit',
  })
  .refine(
    (e) =>
      !e.flags.includes('mender') ||
      (e.healRadius !== undefined && e.healPercentPerSecond !== undefined),
    { message: 'mender enemies must declare healRadius and healPercentPerSecond' },
  );

// ── Waves (TRD-02 §7) ─────────────────────────────────────────────────────────────────

export const WaveGroupSchema = z.object({
  enemy: EnemyIdSchema,
  count: z.number().int().positive(),
  /** Seconds between spawns within the group. */
  spacing: z.number().positive(),
  /** Seconds after wave start. */
  delay: z.number().min(0),
  hpMultiplier: z.number().positive().default(1),
  pathId: z.number().int().nonnegative().optional(),
});

export const WaveDefSchema = z.object({
  wave: z.number().int().positive(),
  groups: z.array(WaveGroupSchema).min(1),
  isBoss: z.boolean().default(false),
});

// ── Maps (TRD-02 §8) ──────────────────────────────────────────────────────────────────

export const WaypointSchema = z.object({ x: z.number(), y: z.number() });

export const PathSchema = z.object({
  id: z.number().int().nonnegative(),
  waypoints: z.array(WaypointSchema).min(2),
});

/**
 * What a human authors.
 *
 * Authors do NOT hand-write the 576-entry tile layers or the 32×18 build mask — those are
 * compiled from the path polyline plus explicit blocker rectangles (R-02-12c). Hand-drawing
 * a mask that agrees with a polyline is tedious and silently wrong when it doesn't, and
 * generated daily maps have to be compiled by the same code path anyway (TRD-13 §4.4).
 */
export const MapSourceSchema = z.object({
  id: MapIdSchema,
  name: z.string().min(2),
  biome: BiomeIdSchema,
  paths: z.array(PathSchema).min(1),
  core: WaypointSchema,
  /** Inclusive tile rectangles that are unbuildable decorative terrain. */
  blockers: z
    .array(
      z.object({
        x0: z.number().int(),
        y0: z.number().int(),
        x1: z.number().int(),
        y1: z.number().int(),
      }),
    )
    .default([]),
  modifier: z.string().optional(),
});

/** The compiled, runtime form. `buildMask` is the ONLY runtime authority (R-02-12). */
export const MapDefSchema = z.object({
  id: MapIdSchema,
  name: z.string(),
  biome: BiomeIdSchema,
  width: z.literal(MAP_WIDTH),
  height: z.literal(MAP_HEIGHT),
  paths: z.array(PathSchema).min(1),
  core: WaypointSchema,
  spawns: z.array(WaypointSchema).min(1),
  /** Row-major. '.' buildable, '#' blocked, 'p' path, 's' spawn, 'c' core. */
  buildMask: z.array(z.string().length(MAP_WIDTH)).length(MAP_HEIGHT),
  groundLayer: z.array(z.number().int()).length(MAP_WIDTH * MAP_HEIGHT),
  overlayLayer: z.array(z.number().int()).length(MAP_WIDTH * MAP_HEIGHT),
  modifier: z.string().optional(),
  /** Telemetry and run-summary display only — no engine behaviour branches on it. */
  generated: z.boolean().default(false),
});

// ── Generator data (TRD-02 §8b) ───────────────────────────────────────────────────────

export const BiomeDefSchema = z.object({
  id: BiomeIdSchema,
  name: z.string().min(2),
  tileset: z.string().min(1),
  palette: z.record(z.string(), HexColor),
  terrainDensity: z.number().min(0).max(0.4),
  decorDensity: z.number().min(0).max(1),
  corePlacement: z.enum(['center', 'edge-biased', 'any']),
  weight: z.number().positive(),
});

export const MutatorDefSchema = z.object({
  id: MutatorIdSchema,
  name: z.string().min(2),
  /** One sentence, shown on the daily screen BEFORE the run starts (TRD-13 R-13-32). */
  description: z.string().min(8).max(160),
  effects: z.array(SynergyEffectSchema).default([]),
  enemyModifiers: z
    .array(
      z.object({
        stat: z.enum(['hp', 'speed', 'armor']),
        op: z.enum(['mul', 'add']),
        value: z.number(),
      }),
    )
    .default([]),
  /** Compensates the difficulty the mutator adds or removes (TRD-13 R-13-31). */
  threatBudgetMultiplier: z.number().positive(),
  weight: z.number().positive(),
});

export const GenTuningSchema = z
  .object({
    pathLength: z.tuple([z.number().positive(), z.number().positive()]),
    waveCount: z.tuple([z.number().int().positive(), z.number().int().positive()]),
    baseThreat: z.number().positive(),
    threatGrowth: z.number().min(1),
    /** Extra compounding growth applied only from `lateGrowthFromWave` onward. */
    lateThreatGrowth: z.number().min(1),
    lateGrowthFromWave: z.number().int().positive(),
    spikeMultiplier: z.number().positive(),
    reliefMultiplier: z.number().positive(),
    terrainDensity: z.tuple([z.number().min(0), z.number().min(0)]),
    maxAttempts: z.number().int().positive(),
    probeTargetWave: z.number().int().positive(),
  })
  .refine((t) => t.pathLength[0] < t.pathLength[1], { message: 'pathLength must be [min, max]' })
  .refine((t) => t.waveCount[0] < t.waveCount[1], { message: 'waveCount must be [min, max]' })
  .refine((t) => t.terrainDensity[0] < t.terrainDensity[1], {
    message: 'terrainDensity must be [min, max]',
  });

export const GenDataSchema = z.object({
  biomes: z.array(BiomeDefSchema).min(1),
  threatCosts: z.record(EnemyIdSchema, z.number().positive()),
  /** Earliest wave an archetype may appear (TRD-13 R-13-25). */
  archetypeGates: z.record(EnemyIdSchema, z.number().int().positive()),
  bossPool: z
    .array(z.object({ enemy: EnemyIdSchema, minWave: z.number().int().positive() }))
    .min(1),
  /** The boss every run ends on, overriding the draw on the last wave. */
  finalBoss: EnemyIdSchema.optional(),
  mutators: z.array(MutatorDefSchema).min(1),
  tuning: GenTuningSchema,
});

// ── Inferred types ────────────────────────────────────────────────────────────────────

export type ClassDef = z.infer<typeof ClassDefSchema>;
export type TowerDef = z.infer<typeof TowerDefSchema>;
export type EnemyDef = z.infer<typeof EnemyDefSchema>;
export type WaveDef = z.infer<typeof WaveDefSchema>;
export type MapSource = z.infer<typeof MapSourceSchema>;
export type MapDef = z.infer<typeof MapDefSchema>;
export type BiomeDef = z.infer<typeof BiomeDefSchema>;
export type MutatorDef = z.infer<typeof MutatorDefSchema>;
export type GenData = z.infer<typeof GenDataSchema>;
export type PathDef = z.infer<typeof PathSchema>;
